/** The name fields a user picker needs from any user record. */
export interface UserOptionFields {
  email?: string | null
  first_name?: string | null
  last_name?: string | null
}

export interface UserOptionText {
  /** "First Last", or the email when the user has no name. */
  primary: string
  /** The email under the name; omitted when the primary line already is the email. */
  secondary?: string
}

/**
 * How a user reads in a picker: the full name on top and the email underneath,
 * falling back to the email alone for a user without a name.
 */
export const getUserOptionText = (user: UserOptionFields): UserOptionText => {
  const email = user.email ?? ''
  const fullName = `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim()
  if (!fullName) {
    return {primary: email}
  }
  return email ? {primary: fullName, secondary: email} : {primary: fullName}
}

/**
 * The users answering for `emails`, keyed by each email as given. Emails compare
 * case-insensitively, as the organizations `users/?emails=` filter does. A user
 * whose email was not asked for is left out, so a response that is not filtered
 * (an organizations deploy without that filter answers a plain page of the
 * directory) can never put a name to the wrong email.
 */
export const matchUsersByEmail = <T extends UserOptionFields>(
  emails: string[],
  users: T[] = [],
): Map<string, T> => {
  const byEmail = new Map<string, T>()
  users.forEach((user) => {
    if (user.email) {
      byEmail.set(user.email.toLowerCase(), user)
    }
  })
  const matched = new Map<string, T>()
  emails.forEach((email) => {
    const user = byEmail.get(email.toLowerCase())
    if (user) {
      matched.set(email, user)
    }
  })
  return matched
}
